import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import useHttp from '../../hooks/useHttp';
import FunZoneLogo from '../components/Login/FunZoneLogo';

/**
 * Public approval page behind the links in the device-type request mail.
 *
 * The signed token in the URL is the only credential — the approver does not have
 * to be logged in on the device reading the mail. A single link shows the one
 * request it was sent for; a batch link shows every request still pending, each
 * with its own checkbox, so several can be waved through in one go.
 */

const DEVICE_TYPE_LABELS = {
  company: 'Firmengerät',
  counter: 'Kassensystem',
  private: 'Privates Gerät',
};

const formatDateTime = value =>
  value && dayjs(value).isValid() ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—';

const DeviceApproval = () => {
  const { token } = useParams();
  const { sendRequest } = useHttp();

  const [scope, setScope] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const res = await sendRequest({
        url: `/api/v1/deviceTypeRequest/approval/${encodeURIComponent(token)}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        rethrowError: true,
      });
      setScope(res);
      setSelected((res?.requests || []).filter(r => r.status === 'pending').map(r => r._id));
    } catch (err) {
      const code = err?.code;
      setLoadError(
        code === 'DEVICE_APPROVAL_LINK_EXPIRED'
          ? 'Dieser Freigabe-Link ist abgelaufen. Bitte entscheide die Anfrage im Management unter „Gerätetyp-Anfragen".'
          : code === 'DEVICE_TYPE_REQUEST_NOT_FOUND'
            ? 'Diese Anfrage gibt es nicht mehr.'
            : 'Dieser Freigabe-Link ist ungültig.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [sendRequest, token]);

  useEffect(() => {
    load();
  }, [load]);

  const pending = (scope?.requests || []).filter(r => r.status === 'pending');

  const decide = async decision => {
    if (selected.length === 0) {
      setActionError('Bitte wähle mindestens eine Anfrage aus.');
      return;
    }
    setActionError('');
    setBusy(decision);
    try {
      const res = await sendRequest({
        url: `/api/v1/deviceTypeRequest/approval/${encodeURIComponent(token)}/${decision}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { requestIds: selected },
        rethrowError: true,
      });
      setDone({ decision, count: res?.decided ?? selected.length });
      await load();
    } catch (err) {
      setActionError(err?.message || 'Die Entscheidung konnte nicht gespeichert werden.');
    } finally {
      setBusy(null);
    }
  };

  const toggle = id =>
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  return (
    <Grid
      container
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '700px',
        width: '95%',
      }}
      rowSpacing={2}
    >
      <Grid item xs={12} sx={{ mb: 2 }}>
        <FunZoneLogo />
      </Grid>

      <Grid item xs={12}>
        <Typography variant='h5' component='h2' align='center' sx={{ mb: 1 }}>
          Gerätetyp-Freigabe
        </Typography>
        {scope?.approverName && (
          <Typography variant='body2' align='center' sx={{ mb: 2, color: 'text.secondary' }}>
            Hallo {scope.approverName}
          </Typography>
        )}
      </Grid>

      {isLoading ? (
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Grid>
      ) : loadError ? (
        <Grid item xs={12}>
          <Alert severity='error'>{loadError}</Alert>
        </Grid>
      ) : (
        <>
          {done && (
            <Grid item xs={12}>
              <Alert severity='success'>
                {done.count} {done.count === 1 ? 'Anfrage' : 'Anfragen'}{' '}
                {done.decision === 'approve' ? 'freigegeben' : 'abgelehnt'}. Das Gerät übernimmt die
                Freigabe beim nächsten Anmelden automatisch.
              </Alert>
            </Grid>
          )}

          {pending.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity='info'>
                Hier ist gerade nichts mehr zu entscheiden — alle Anfragen dieses Links sind bereits
                bearbeitet.
              </Alert>
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {scope?.kind === 'batch'
                    ? `${pending.length} offene ${pending.length === 1 ? 'Anfrage' : 'Anfragen'} — wähle aus, was freigegeben werden soll.`
                    : 'Bitte prüfe die Anfrage und entscheide.'}
                </Typography>
              </Grid>

              {pending.map(request => (
                <Grid item xs={12} key={request._id}>
                  <Paper variant='outlined' sx={{ p: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selected.includes(request._id)}
                          onChange={() => toggle(request._id)}
                          disabled={busy !== null}
                        />
                      }
                      label={
                        <Typography variant='subtitle2'>
                          {request.requesterName || 'Unbekannt'} —{' '}
                          {DEVICE_TYPE_LABELS[request.requestedDeviceType] ||
                            request.requestedDeviceType}
                        </Typography>
                      }
                    />
                    <Box sx={{ pl: 4, color: 'text.secondary' }}>
                      {request.deviceInfo?.label && (
                        <Typography variant='caption' display='block'>
                          Bezeichnung: {request.deviceInfo.label}
                        </Typography>
                      )}
                      {request.deviceInfo?.platform && (
                        <Typography variant='caption' display='block'>
                          Plattform: {request.deviceInfo.platform}
                        </Typography>
                      )}
                      {request.deviceInfo?.userAgent && (
                        <Typography variant='caption' display='block' sx={{ wordBreak: 'break-all' }}>
                          Browser: {request.deviceInfo.userAgent}
                        </Typography>
                      )}
                      {request.deviceInfo?.ip && (
                        <Typography variant='caption' display='block'>
                          IP: {request.deviceInfo.ip}
                        </Typography>
                      )}
                      {request.reason && (
                        <Typography variant='caption' display='block'>
                          Begründung: {request.reason}
                        </Typography>
                      )}
                      <Typography variant='caption' display='block'>
                        Angefragt am {formatDateTime(request.createdAt)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}

              {actionError && (
                <Grid item xs={12}>
                  <Alert severity='error'>{actionError}</Alert>
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}
              >
                <Button
                  variant='outlined'
                  onClick={() => decide('reject')}
                  disabled={busy !== null || selected.length === 0}
                >
                  {busy === 'reject' ? 'Wird gespeichert…' : 'Ablehnen'}
                </Button>
                <Button
                  onClick={() => decide('approve')}
                  disabled={busy !== null || selected.length === 0}
                >
                  {busy === 'approve'
                    ? 'Wird gespeichert…'
                    : selected.length > 1
                      ? `${selected.length} freigeben`
                      : 'Freigeben'}
                </Button>
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  );
};

export default DeviceApproval;
